import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Input,
  Textarea
} from '../ui';
import { solicitarCambioRoster } from '../../lib/services/torneoRoster';
import { fixMojibake } from '../../lib/utils';

const TIPO_OPTIONS = [
  { value: 'alta', label: 'Agregar jugador' },
  { value: 'baja', label: 'Quitar jugador' },
  { value: 'edicion', label: 'Cambiar dorsal/posición' }
];

const SolicitarCambioRosterDialog = ({
  open,
  onOpenChange,
  inscripcionId,
  grupoJugadores = [],
  rosterMap,
  onSolicitado
}) => {
  const [tipo, setTipo] = useState('alta');
  const [jugadorId, setJugadorId] = useState('');
  const [dorsal, setDorsal] = useState('');
  const [posicion, setPosicion] = useState('');
  const [nota, setNota] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [errMsg, setErrMsg] = useState(null);

  useEffect(() => {
    if (open) {
      setTipo('alta');
      setJugadorId('');
      setDorsal('');
      setPosicion('');
      setNota('');
      setErrMsg(null);
    }
  }, [open]);

  useEffect(() => {
    setJugadorId('');
  }, [tipo]);

  const jugadoresOptions = useMemo(() => {
    if (tipo === 'alta') {
      return grupoJugadores.filter((j) => {
        const r = rosterMap?.get(j.id);
        return !r || r.status === 'no_convocado';
      });
    }
    return grupoJugadores.filter((j) => rosterMap?.get(j.id)?.status === 'convocado');
  }, [tipo, grupoJugadores, rosterMap]);

  const jugadorSeleccionado = jugadoresOptions.find((j) => j.id === jugadorId) || null;
  const puedeEnviar = Boolean(tipo && jugadorId) && !enviando;

  const handleSubmit = async () => {
    if (!jugadorId || enviando) return;
    setEnviando(true);
    setErrMsg(null);
    try {
      const dorsalNum = dorsal === '' ? null : parseInt(dorsal, 10);
      const { error } = await solicitarCambioRoster(inscripcionId, {
        tipo,
        jugadorId,
        nombre: jugadorSeleccionado?.nombre_completo || null,
        dorsal: Number.isNaN(dorsalNum) ? null : dorsalNum,
        posicion: posicion.trim() || null,
        nota: nota.trim() || null
      });
      if (error) {
        setErrMsg(error.message || 'No se pudo enviar la solicitud.');
        return;
      }
      onSolicitado?.();
    } catch (err) {
      console.error('Error solicitando cambio de roster:', err);
      setErrMsg(err?.message || 'No se pudo enviar la solicitud.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Solicitar cambio de roster</DialogTitle>
          <DialogDescription>
            El roster está congelado. La organizadora revisará y aprobará tu solicitud.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Tipo de cambio</Label>
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIPO_OPTIONS.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Jugador</Label>
            {jugadoresOptions.length === 0 ? (
              <p className="text-xs text-muted-foreground py-2">
                No hay jugadores disponibles para este tipo de cambio.
              </p>
            ) : (
              <Select value={jugadorId} onValueChange={setJugadorId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecciona un jugador" />
                </SelectTrigger>
                <SelectContent>
                  {jugadoresOptions.map((j) => (
                    <SelectItem key={j.id} value={j.id}>
                      {fixMojibake(j.nombre_completo || 'Sin nombre')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {(tipo === 'alta' || tipo === 'edicion') && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="cambio-roster-dorsal">Dorsal</Label>
                <Input
                  id="cambio-roster-dorsal"
                  type="number"
                  placeholder="Ej. 10"
                  value={dorsal}
                  onChange={(e) => setDorsal(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cambio-roster-posicion">Posición</Label>
                <Input
                  id="cambio-roster-posicion"
                  placeholder="Ej. Delantero"
                  value={posicion}
                  onChange={(e) => setPosicion(e.target.value)}
                />
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="cambio-roster-nota">Nota (opcional)</Label>
            <Textarea
              id="cambio-roster-nota"
              placeholder="Motivo de la solicitud..."
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              rows={3}
            />
          </div>

          {errMsg && <p className="text-sm text-destructive">{errMsg}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={enviando}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!puedeEnviar}>
            {enviando ? 'Enviando...' : 'Solicitar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SolicitarCambioRosterDialog;
